/* Left rail — searchable pair list with ALL / INR / USDT quote filters (CoinDCX-style). */
import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { api, wsBaseUrl } from "@/lib/api";
import CoinIcon from "@/components/CoinIcon";

const C = {
  surface: "#131A24",
  surface2: "#0F151D",
  border: "#1F2937",
  borderSoft: "#1A2230",
  text: "#E5E7EB",
  sub: "#94A3B8",
  dim: "#64748B",
  green: "#22C55E",
  red: "#EF4444",
  blueLight: "#3B82F6",
};

const QUOTE_TABS = ["ALL", "INR", "USDT"];

function fmtPrice(n, quote) {
  const v = Number(n);
  if (!Number.isFinite(v) || v === 0) return "—";
  if (quote === "INR") return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  if (v < 0.01) return v.toFixed(6);
  if (v < 1) return v.toFixed(4);
  return v.toLocaleString("en-US", { maximumFractionDigits: 4 });
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}%`;
}

export default function PairListSidebar({ currentPair, onSelect }) {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [tickers, setTickers] = useState({});
  const [quoteTab, setQuoteTab] = useState("USDT");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    api.get("/api/spot/pairs")
      .then(({ data }) => {
        if (cancelled) return;
        setPairs(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setPairs([]);
        setLoadError(err?.message || "Could not load pairs");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    let ws;
    let timer;
    let backoff = 800;
    const apply = (rows) => {
      if (!alive || !Array.isArray(rows)) return;
      setTickers((prev) => {
        const map = { ...prev };
        rows.forEach((t) => {
          if (t?.pair) map[t.pair] = t;
        });
        return map;
      });
    };
    const connect = () => {
      try {
        ws = new WebSocket(`${wsBaseUrl()}/api/ws/tickers`);
      } catch {
        return;
      }
      ws.onopen = () => { backoff = 800; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "tickers_snapshot" || msg.type === "tickers_update") {
            apply(msg.tickers);
          } else if (msg.type === "ticker" && msg.pair) {
            apply([msg]);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!alive) return;
        timer = setTimeout(connect, backoff);
        backoff = Math.min(Math.round(backoff * 1.7), 8000);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return pairs.filter((p) => {
      if (quoteTab !== "ALL" && p.quote !== quoteTab) return false;
      if (!ql) return true;
      return (
        p.pair.toLowerCase().includes(ql) ||
        p.base.toLowerCase().includes(ql) ||
        p.quote.toLowerCase().includes(ql)
      );
    });
  }, [pairs, quoteTab, q]);

  return (
    <div
      className="flex flex-col h-full min-h-0 border-r"
      style={{ background: C.surface2, borderColor: C.border }}
      data-testid="trade-pair-sidebar"
    >
      <div className="px-3 py-2.5 border-b" style={{ borderColor: C.borderSoft }}>
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md" style={{ background: C.surface, border: `1px solid ${C.borderSoft}` }}>
          <Search size={14} style={{ color: C.dim }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Coins"
            data-testid="trade-pair-sidebar-search"
            className="bg-transparent outline-none text-[12px] flex-1"
            style={{ color: C.text }}
          />
        </div>
        <div className="flex gap-1 mt-2">
          {QUOTE_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setQuoteTab(tab)}
              data-testid={`trade-quote-tab-${tab.toLowerCase()}`}
              className="flex-1 h-[26px] rounded text-[10px] font-bold uppercase tracking-wider transition"
              style={{
                background: quoteTab === tab ? C.blueLight : "transparent",
                color: quoteTab === tab ? "#fff" : C.sub,
                border: `1px solid ${quoteTab === tab ? C.blueLight : C.borderSoft}`,
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-[1fr_auto_56px] gap-1 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider" style={{ color: C.dim }}>
        <span>Pair</span>
        <span className="text-right">Price</span>
        <span className="text-right">24h</span>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto trade-scroll">
        {loading ? (
          <div className="px-3 py-8 text-center text-[12px]" style={{ color: C.dim }}>
            Loading pairs…
          </div>
        ) : loadError ? (
          <div className="px-3 py-8 text-center text-[12px]" style={{ color: C.red }}>
            {loadError}
          </div>
        ) : null}
        {!loading && !loadError && filtered.map((p) => {
          const t = tickers[p.pair];
          const ch = t?.change_pct_24h;
          const up = (ch ?? 0) >= 0;
          const active = p.pair === currentPair;
          return (
            <button
              key={p.pair}
              type="button"
              onClick={() => onSelect(p)}
              data-testid={`trade-sidebar-pair-${p.base}-${p.quote}`}
              className="w-full grid grid-cols-[1fr_auto_56px] gap-1 items-center px-3 py-2 text-left transition"
              style={{
                background: active ? "rgba(59,130,246,0.12)" : "transparent",
                borderLeft: active ? `2px solid ${C.blueLight}` : "2px solid transparent",
              }}
            >
              <div className="min-w-0 flex items-center gap-2">
                <CoinIcon symbol={p.base} image={p.icon} size={22} />
                <div className="min-w-0">
                  <div className="text-[12px] font-bold truncate" style={{ color: C.text }}>
                    {p.base}
                    <span className="text-[10px] font-normal" style={{ color: C.dim }}>/{p.quote}</span>
                  </div>
                </div>
              </div>
              <span className="text-[11px] font-mono text-right" style={{ color: C.text }}>
                {t ? fmtPrice(t.last, p.quote) : "—"}
              </span>
              <span className="text-[10px] font-bold font-mono text-right" style={{ color: ch == null ? C.dim : up ? C.green : C.red }}>
                {ch == null ? "—" : fmtPct(ch)}
              </span>
            </button>
          );
        })}
        {!loading && !loadError && filtered.length === 0 ? (
          <div className="px-3 py-8 text-center text-[12px]" style={{ color: C.dim }}>
            No pairs match.
          </div>
        ) : null}
      </div>
    </div>
  );
}
